import { forwardRef, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { v4 } from 'uuid';
import { FloatingLabel } from '../floating-label/index.js';
import { InputBaseStyled, InputMemoized } from './styles.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { GenericInput } from '../input-base/components.js';
import { InputSize } from '../../enumerations/input-size.js';

const TextField = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    id,
    children,
    label,
    placeholder,
    svgIcon,
    customIcon,
    prefix,
    value,
    size = InputSize.Large,
    semanticState,
    onBlur,
    onFocus,
    onChange,
    // props that need to be passed to mask rather than child input
    onPaste,
    onMouseDown,
    disabled,
    isLoading,
    isLabelHidden,
    isErrorStateOverriden,
    readOnly,
    // rest of props to be passed to input
    ...props
  } = _ref;
  const [isFocused, setIsFocused] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const [isElevated, setIsElevated] = useState(!value && label ? true : Boolean(value));
  const previousValue = useRef(null);
  const localInputRef = useRef(null);
  const inputRef = ref ?? localInputRef;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const textFieldId = useMemo(() => id || v4(), [id]);
  const handleMouseDown = useCallback(event => {
    const isRefCallback = typeof inputRef === "function";
    if (event.target !== inputRef.current && !isRefCallback) {
      event.preventDefault();
      inputRef.current?.focus();
    }
    onMouseDown?.(event);
  }, []);
  const handleChange = useCallback(event => {
    setCurrentValue(event.target.value);
    onChange?.(event);
  }, [onChange]);
  const handleFocus = useCallback(event => {
    setIsElevated(true);
    setIsFocused(true);
    onFocus?.(event);
  }, [onFocus]);
  const handleBlur = useCallback(event => {
    setIsElevated(Boolean(currentValue));
    setIsFocused(false);
    onBlur?.(event);
  }, [currentValue, onBlur]);
  const shouldShowPlaceholder = Boolean(label && (isFocused || isLabelHidden) && placeholder || !label && placeholder);
  useEffect(() => {
    if (previousValue.current !== value) {
      setCurrentValue(value);
    }
    previousValue.current = value;
  }, [value]);
  useEffect(() => {
    setIsElevated(Boolean(currentValue) || isFocused);
  }, [currentValue, isFocused]);
  useEffect(() => {
    if (isElevated && isFocused) {
      inputRef.current?.focus();
    }
  }, [children]);
  const isPrefixVisible = useMemo(() => {
    const isLabelPresent = Boolean(label);
    const isLabelPresentAndElevated = isLabelPresent && isElevated;
    const isPrefixSupplied = Boolean(prefix);
    return isPrefixSupplied && (!isLabelPresent || isLabelPresentAndElevated || isFocused);
  }, [label, prefix, isFocused, isElevated]);
  return /*#__PURE__*/jsxs(InputBaseStyled, {
    id: textFieldId,
    isIdNotUsedDirectly: true,
    isLoading: isLoading,
    className: "otto-text-field-outer-container",
    isFocused: isFocused,
    isDisabled: disabled,
    isPrefixVisible: isPrefixVisible,
    svgIcon: svgIcon,
    semanticState: semanticState,
    customIcon: customIcon,
    prefix: prefix,
    size: size,
    isErrorStateOverriden: isErrorStateOverriden,
    onMouseDown: handleMouseDown,
    children: [Boolean(label) && /*#__PURE__*/jsx(FloatingLabel, {
      id: `${textFieldId}-label`,
      isElevated: isElevated,
      isHidden: isLabelHidden,
      isWithIcon: Boolean(svgIcon ?? customIcon),
      isErrorStateOverriden: isErrorStateOverriden,
      htmlFor: textFieldId,
      children: label
    }), /*#__PURE__*/jsx(GenericInput, {
      children: /*#__PURE__*/jsx(InputMemoized, {
        ref: inputRef,
        ...props,
        id: textFieldId,
        placeholder: shouldShowPlaceholder ? placeholder : undefined,
        value: value ?? currentValue ?? "",
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onPaste: onPaste,
        onMouseDown: onMouseDown,
        disabled: disabled,
        readOnly: readOnly,
        "aria-readonly": readOnly,
        "aria-busy": isLoading,
        "aria-labelledby": label ? `${textFieldId}-label` : undefined
      })
    }), children]
  });
});

export { TextField };
//# sourceMappingURL=components.js.map
